import React from 'react'
import { saveAs } from 'file-saver'
import Papa from 'papaparse'
import { Button, Dropdown, Space } from 'antd'
import { DownloadOutlined, DownOutlined } from '@ant-design/icons'
import { useUserPlantInfo } from '../hooks/Plants'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import objectSupport from 'dayjs/plugin/objectSupport'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { useIntl } from 'react-intl'

dayjs.extend(objectSupport)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrBefore)

const createExportDataByRootCause = ({ repairsByUser, intl, timezone }) => {
  return repairsByUser
    .map((repairs) => {
      const reasonHeader = intl.formatMessage({
        id: 'repairByUserReport.reasonHeader',
      })
      const userHeader = intl.formatMessage({
        id: 'repairByUserReport.userHeader',
      })
      const vinCountHeader = intl.formatMessage({
        id: 'repairByUserReport.vinCountHeader',
      })
      return [
        ...repairs.reasons.map((reason) => ({
          [userHeader]: repairs.created_by_id,
          [reasonHeader]: reason.reason,
          [vinCountHeader]: reason.count,
        })),
        {
          [userHeader]: repairs.created_by_id,
          [reasonHeader]: 'Total',
          [vinCountHeader]: repairs.count,
        },
      ]
    })
    .flat()
}

const createDayBuckets = (startAt, endAt, timezone) => {
  const buckets = []
  let current = dayjs(startAt).tz(timezone).startOf('day')
  const last = dayjs(endAt).tz(timezone).startOf('day')

  while (current.isSameOrBefore(last)) {
    buckets.push(current.format('YYYY-MM-DD'))
    current = current.add(1, 'day')
  }

  return buckets
}

const createExportDataByDay = ({
  repairsByUser,
  startAt,
  endAt,
  timezone,
  intl,
}) => {
  /*
    `repairsByUser` is an array of user repair data grouped by user and then by reason.
    For example,
    {
      "T1875JB": {  // user id
      "created_by_id": "T1875JB",
      "reasons": {
      "SHY/ABSENT": [ // reason for repairs
        {
          "annotation": null,
          "component_id": "W_BOTTLE",
          "created_at": 1759312649000, // timestamp
          "created_by_id": "T1875JB",
          "hardware_location_id": "P",
          "reason": "SHY/ABSENT",
          "status": "clean",
          "vehicle_test_result_id": 5555740,
          "vin": "1C6RREFG4SN222620"
        }
      ]
    }
  */
  const userHeader = intl.formatMessage({
    id: 'repairByUserReport.userHeader',
  })
  const totalHeader = intl.formatMessage({
    id: 'repairByUserReport.totalHeader',
  })
  const days = createDayBuckets(startAt, endAt, timezone)
  const rows = []

  repairsByUser.map((repairByUser) => {
    // All repairs for this user
    const repairs = Object.values(repairByUser.reasons)
      .flat()
      .map((r) => Object.values(r.items))
      .flat()

    // Per day buckets
    const dayCounts = days.map(
      (day) =>
        repairs.filter(
          (repair) =>
            dayjs(repair.created_at).tz(timezone).format('YYYY-MM-DD') === day,
        ).length,
    )
    const total = dayCounts.reduce((a, b) => a + b, 0)
    rows.push({
      [userHeader]: repairByUser.created_by_id,
      ...Object.fromEntries(days.map((d, i) => [d, dayCounts[i]])),
      [totalHeader]: total,
    })
  })

  return rows
}

const ExportButton = ({ repairsByUser, startAt, endAt }) => {
  const intl = useIntl()
  const { plantInfo, isLoading } = useUserPlantInfo()

  const exports = [
    {
      label: 'By Root Cause',
      key: 'by-root-cause',
      icon: <DownloadOutlined />,
    },
    {
      label: 'By Day',
      key: 'by-day',
      icon: <DownloadOutlined />,
    },
  ]

  const handleMenuClick = (event) => {
    if (event.key === 'by-root-cause') {
      const data = createExportDataByRootCause({
        repairsByUser,
        intl,
        timezone: plantInfo.timezone,
      })
      const dataString = Papa.unparse(data)
      const blob = new Blob([dataString], { type: 'text/csv;charset=utf-8' })
      saveAs(blob, 'repairs-by-root-cause.csv')
    } else if (event.key === 'by-day') {
      const data = createExportDataByDay({
        repairsByUser,
        intl,
        timezone: plantInfo.timezone,
        startAt,
        endAt,
      })
      const dataString = Papa.unparse(data)
      const blob = new Blob([dataString], { type: 'text/csv;charset=utf-8' })
      saveAs(blob, 'repairs-by-day.csv')
    } else {
      console.warn('Unknown export type', event)
    }
  }
  const menuProps = {
    items: exports,
    onClick: handleMenuClick,
  }

  return (
    <Dropdown
      menu={menuProps}
      disabled={repairsByUser?.length === 0 || isLoading}
    >
      <Button>
        <Space>
          {intl.formatMessage({ id: 'repairByUserReport.exportLabel' })}
          <DownOutlined />
        </Space>
      </Button>
    </Dropdown>
    // <Dropdown.Button

    //   onClick={onClick}
    //   icon={<DownloadOutlined />}
    //   type="primary"
    //   size="small"
    // >

    // </Dropdown.Button>
  )
}

export default ExportButton
